/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useMemo, useCallback } from 'react';
import { gnoment } from 'shared-ui/gnoment';
import isEmpty from 'lodash/isEmpty';
import { PassType, PassTypesTableRowDataType } from '../../types/PassType';
import { getPrice } from '../utils';
import { durationLength } from '../utils/duration_utils';
import { getValidPriceFromDivision } from '../utils/date_time_utils';

const defaultSortFn = (a: PassType, b: PassType) =>
    parseFloat(getPrice(b.periods).replace(/\/.*|\D/g, ' ')) -
    parseFloat(getPrice(a.periods).replace(/\/.*|\D/g, ' '));

function usePassTypesTable(
    selectedPassTypeId: string | undefined,
    passTypes: PassType[],
    passTypesRaw: PassType[],
    status: string | undefined,
    onSelectPass: (id: string) => void,
    onRemovePass: (id: string) => void,
    sortFn: (a: PassType, b: PassType) => number = defaultSortFn
) {
    const filterPassTypes = useCallback((localPassTypes: PassType[]) => {
        return localPassTypes?.filter((item) => {
            const currentDate = gnoment().startOf('day');
            const hasValidStartDate = item.periods?.some((period) =>
                currentDate?.isSameOrAfter(gnoment(period.start_date).startOf('day'))
            );
            return hasValidStartDate;
        });
    }, []);

    const filteredPassTypes = useMemo(() => {
        return filterPassTypes(passTypes)?.sort(sortFn);
    }, [filterPassTypes, passTypes, sortFn]);

    const filteredPassTypesRaw = useMemo(() => {
        return filterPassTypes(passTypesRaw)?.sort(sortFn);
    }, [filterPassTypes, passTypesRaw, sortFn]);

    const isLoading = useMemo(() => {
        return status === 'LOADING';
    }, [status]);

    const getPriceFromPeriods = (periods: PassType['periods']): string => {
        const price = getValidPriceFromDivision(periods, gnoment());
        return isEmpty(price) ? '0' : price;
    };

    const getRowData = useCallback(
        ({
            id,
            name,
            periods,
            used_for: usedFor,
            duration,
            features,
        }: PassType): PassTypesTableRowDataType => {
            const price = getPriceFromPeriods(periods);
            const selectedPass = selectedPassTypeId === id;
            const durationText = durationLength(duration);
            const nonResidentFee = features?.['non_resident_fee']?.fee;

            const handleAddPass = () => {
                if (id) {
                    onSelectPass(id);
                }
            };

            const handleRemoveAllPasses = () => {
                if (id) {
                    onRemovePass(id);
                }
            };

            return {
                id,
                name,
                usedFor,
                duration,
                durationText,
                price,
                selectedPass,
                nonResidentFee,
                handleAddPass,
                handleRemoveAllPasses,
            };
        },
        [onRemovePass, onSelectPass, selectedPassTypeId]
    );

    return { filteredPassTypes, filteredPassTypesRaw, isLoading, getRowData };
}

export default usePassTypesTable;
